import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, CheckCircle2 } from "lucide-react";

const TOPICS = [
  { key: "wyv_news", label: "Witness Your Vote news" },
  { key: "election_reminders", label: "Election reminders" },
  { key: "municipality_alerts", label: "Municipality alerts" },
  { key: "ward_alerts", label: "Ward alerts" },
  { key: "new_candidate_alerts", label: "New candidate alerts" },
  { key: "accountability_investigations", label: "Municipal accountability investigations" },
  // PITA is a Kawartha Lakes-specific external resource — only offered in that context
  { key: "pita_updates", label: "PITA updates (Kawartha Lakes)", kawarthaOnly: true },
  { key: "ontario_election_news", label: "Important Ontario election news" },
];

const isKawartha = (name) => (name || "").toLowerCase().includes("kawartha");

export const AlertsSignup = ({ compact = false, defaultMunicipality = "" }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [municipality, setMunicipality] = useState(defaultMunicipality);
  const [ward, setWard] = useState("");
  const [topics, setTopics] = useState({ wyv_news: true, election_reminders: true, new_candidate_alerts: !!defaultMunicipality });
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const selected = Object.entries(topics).filter(([, v]) => v).map(([k]) => k);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/public/alerts/subscribe", {
        email, name: name || null, municipality: municipality || null, ward: ward || null,
        topics: selected, website: hp,
      });
      setDone(r.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign-up failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <Card className="bg-white">
        <CardContent className="p-8 text-center">
          <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-6 w-6 text-emerald-700" />
          </div>
          <h3 className="font-heading font-bold text-lg" data-testid="alerts-success-title">You're signed up</h3>
          <p className="text-sm text-muted-foreground mt-2">{done}</p>
          <p className="text-xs text-muted-foreground mt-3">
            Alert subscriptions are stored completely separately from voter declaration data.
          </p>
        </CardContent>
      </Card>
    );

  return (
    <Card className="bg-white">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center gap-2.5 mb-2">
          <Bell className="h-5 w-5 text-accent" />
          <h3 className="font-heading font-bold text-lg text-foreground">Sign up for alerts</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-5">
          Choose the updates you want. Subscription data is kept completely separate from voter declarations,
          and you can unsubscribe anytime.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="alerts-email">Email *</Label>
              <Input id="alerts-email" type="email" className="mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="alerts-email-input" />
            </div>
            <div>
              <Label htmlFor="alerts-name">Name (optional)</Label>
              <Input id="alerts-name" className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="alerts-name-input" />
            </div>
          </div>
          {(!compact || defaultMunicipality) && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="alerts-muni">Municipality (optional)</Label>
                <Input id="alerts-muni" className="mt-1.5" placeholder="e.g. City of Kawartha Lakes" value={municipality} onChange={(e) => setMunicipality(e.target.value)} data-testid="alerts-municipality-input" />
              </div>
              <div>
                <Label htmlFor="alerts-ward">Ward (optional)</Label>
                <Input id="alerts-ward" className="mt-1.5" placeholder="e.g. Ward 8" value={ward} onChange={(e) => setWard(e.target.value)} data-testid="alerts-ward-input" />
              </div>
            </div>
          )}
          <fieldset>
            <legend className="text-sm font-semibold text-foreground mb-2">Alert topics *</legend>
            <div className="grid sm:grid-cols-2 gap-2">
              {TOPICS.filter((t) => !t.kawarthaOnly || isKawartha(municipality) || isKawartha(defaultMunicipality)).map((t) => (
                <label key={t.key} className="flex items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50">
                  <Checkbox
                    checked={!!topics[t.key]}
                    onCheckedChange={(v) => setTopics((s) => ({ ...s, [t.key]: !!v }))}
                    data-testid={`alerts-topic-${t.key}`}
                  />
                  <span className="text-xs font-medium text-foreground">{t.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <Button type="submit" className="w-full h-11" disabled={busy || !/.+@.+\..+/.test(email) || selected.length === 0} data-testid="alerts-submit-button">
            {busy ? "Signing up…" : "Sign Up for Alerts"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
